import {
  FundingBenefitTypeItem,
  FundingSettingsAutomation,
} from '../../api/fundingApi'

const automationKey = (a: FundingSettingsAutomation) =>
  `${a.program}:${a.program_label}`

/** Exact catalog / GetBenefit application display name. */
export const displayFundingProgramLabel = (name: string) => name.trim()

export const isMpoppType = (type: FundingBenefitTypeItem) =>
  (type.workflow_key || '').toLowerCase() === 'mpopp'

/**
 * Whether Settings shows a template count / expand for this program.
 * MPOPP (and similar auto-submit-only programs) do not use Labra templates —
 * show N/A instead of 0.
 */
export const supportsFundingTemplates = (
  type: FundingBenefitTypeItem | undefined
): boolean => {
  if (!type) return false
  if (isMpoppType(type)) return false
  return true
}

/** PATCH / settings key: mpopp | poc slugs, else exact catalog Name. */
export const automationProgramKey = (type: FundingBenefitTypeItem): string => {
  const wk = (type.workflow_key || '').trim().toLowerCase()
  if (wk === 'mpopp' || wk === 'poc') return wk
  return type.program
}

/**
 * Auto-submit can be toggled for any enrolled program, with or without templates.
 * (Synthesized automation covers programs not yet on the settings API.)
 */
export const canToggleAutoSubmit = (
  type: FundingBenefitTypeItem | undefined,
  _templateCount = 0,
  _hasServerAutomation = false
): boolean => Boolean(type)

/** Build a toggle payload when settings API has not enrolled this program yet. */
export const synthesizeAutomation = (
  type: FundingBenefitTypeItem
): FundingSettingsAutomation => ({
  program: automationProgramKey(type),
  program_label: displayFundingProgramLabel(type.program),
  auto_submit: false,
  enabled: true,
  auto_submission_limit_usd: null,
  description: type.description ?? null,
  eligibility_criteria: type.eligibility_criteria ?? null,
  auto_submit_criteria: null,
})

/**
 * Browse / Settings targets: GetBenefit applications only.
 * Exclude cash claims and AWS Funding Template drafts (``create_template``).
 */
export const isBrowsableFundingType = (type: FundingBenefitTypeItem) =>
  !type.cash_claim && !type.create_template

/**
 * HubSpot deal-card seven — GetBenefit application ids (not Funding Template drafts).
 * MPOPP Grow / POC / MDF first; MAP, GenAI POC/MDF, ISV Workload Migration follow.
 */
export const SETTINGS_CORE_BENEFIT_IDS: readonly string[] = [
  'ben-2dg4hus8pgrmg2', // MPOPP Grow
  'ben-un8hh6qdrxqx50', // Proof Of Concept Funding program
  'ben-yf5bjxoqwgnry6', // Marketing Development Fund Funding program
  'ben-3en0tc0lspcqw4', // Migration Acceleration Program Funding program
  'ben-mmyf7i4fn9eto2', // Generative AI Proof of Concept
  'ben-c77u1j7s2rmy52', // Generative AI Marketing Development Fund
  'ben-gr123za3behub0', // ISV Workload Migration Program
]

/** Catalog name duplicates → primary Settings application id. */
const SETTINGS_CORE_ALIAS_TO_PRIMARY: Readonly<Record<string, string>> = {
  'ben-6neamoq4608dfi': 'ben-mmyf7i4fn9eto2', // GenAI POC under POC parent
}

/**
 * Legacy Funding Template draft ids still used by existing Labra templates.
 * Map application ↔ template so template counts stay correct.
 */
const APPLICATION_TO_TEMPLATE_ID: Readonly<Record<string, string>> = {
  'ben-un8hh6qdrxqx50': 'ben-tqlposefeyw51i',
  'ben-yf5bjxoqwgnry6': 'ben-za6cgj1kgyf61n',
  'ben-3en0tc0lspcqw4': 'ben-03nva1kjheja18',
  'ben-gr123za3behub0': 'ben-r6t9b87ttgox42',
}

const TEMPLATE_TO_APPLICATION_ID: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(APPLICATION_TO_TEMPLATE_ID).map(([app, tpl]) => [tpl, app])
)

const CORE_BENEFIT_ID_SET: ReadonlySet<string> = new Set([
  ...SETTINGS_CORE_BENEFIT_IDS,
  ...Object.keys(SETTINGS_CORE_ALIAS_TO_PRIMARY),
])

/** Automation slug → GetBenefit application id. */
const AUTOMATION_SLUG_TO_CORE_ID: Readonly<Record<string, string>> = {
  poc: 'ben-un8hh6qdrxqx50',
  mdf: 'ben-yf5bjxoqwgnry6',
  mpopp: 'ben-2dg4hus8pgrmg2',
}

export const isSettingsCoreProgram = (type: FundingBenefitTypeItem): boolean =>
  CORE_BENEFIT_ID_SET.has(type.id)

/** Benefit ids that share templates with this Settings row (application + legacy template). */
export const relatedBenefitIdsForSettings = (
  benefitId: string
): ReadonlySet<string> => {
  const ids = new Set<string>([benefitId])
  const primary = SETTINGS_CORE_ALIAS_TO_PRIMARY[benefitId] ?? benefitId
  ids.add(primary)
  const tpl = APPLICATION_TO_TEMPLATE_ID[primary]
  if (tpl) ids.add(tpl)
  const app = TEMPLATE_TO_APPLICATION_ID[benefitId]
  if (app) ids.add(app)
  return ids
}

export const filterBrowsablePrograms = (
  types: FundingBenefitTypeItem[]
): FundingBenefitTypeItem[] => types.filter(isBrowsableFundingType)

/** Attach automation by exact label, else fixed poc/mdf/mpopp application ids. */
export const findEligibleForAutomation = (
  program: FundingSettingsAutomation,
  types: FundingBenefitTypeItem[]
): FundingBenefitTypeItem | undefined => {
  const exact = types.find(t => t.program === program.program_label)
  if (exact) return exact

  const slug = (program.program || '').trim().toLowerCase()
  const coreId = AUTOMATION_SLUG_TO_CORE_ID[slug]
  if (!coreId) return undefined
  return types.find(
    t =>
      t.id === coreId ||
      SETTINGS_CORE_ALIAS_TO_PRIMARY[t.id] === coreId ||
      (slug === 'mpopp' && isMpoppType(t) && t.program === program.program_label)
  )
}

export type ProgramRow = {
  key: string
  label: string
  description?: string | null
  eligibilityCriteria?: string | null
  automation?: FundingSettingsAutomation
  eligible?: FundingBenefitTypeItem
}

const rowDescription = (
  automation: FundingSettingsAutomation | undefined,
  eligible: FundingBenefitTypeItem | undefined
) =>
  automation?.description?.trim() ||
  eligible?.description?.trim() ||
  null

const rowEligibility = (
  automation: FundingSettingsAutomation | undefined,
  eligible: FundingBenefitTypeItem | undefined
) =>
  automation?.eligibility_criteria?.trim() ||
  eligible?.eligibility_criteria?.trim() ||
  null

const coreSortIndex = (type: FundingBenefitTypeItem): number => {
  const primary = SETTINGS_CORE_ALIAS_TO_PRIMARY[type.id] ?? type.id
  const idx = SETTINGS_CORE_BENEFIT_IDS.indexOf(primary)
  return idx >= 0 ? idx : SETTINGS_CORE_BENEFIT_IDS.length
}

/**
 * Settings starting matrix:
 * 1. Core seven GetBenefit application ids
 * 2. Attach automation when it matches
 * 3. Non-core after a template exists
 * 4. Browse-pinned programs always at the bottom (pin order)
 */
export const buildProgramRows = (
  automation: FundingSettingsAutomation[],
  eligibleTypes: FundingBenefitTypeItem[],
  benefitIdsWithTemplates: ReadonlySet<string> = new Set(),
  pinnedBenefitIds: readonly string[] = []
): ProgramRow[] => {
  const browsable = filterBrowsablePrograms(eligibleTypes)
  const usedEligibleIds = new Set<string>()
  const rows: ProgramRow[] = []
  const pinnedIdSet = new Set(pinnedBenefitIds)

  const automationByEligibleId = new Map<string, FundingSettingsAutomation>()
  for (const program of automation) {
    const eligible = findEligibleForAutomation(program, browsable)
    if (eligible) {
      automationByEligibleId.set(eligible.id, program)
    }
  }

  const hasTemplateFor = (type: FundingBenefitTypeItem) => {
    for (const id of relatedBenefitIdsForSettings(type.id)) {
      if (benefitIdsWithTemplates.has(id)) return true
    }
    return false
  }

  const isPinned = (type: FundingBenefitTypeItem) => {
    for (const id of relatedBenefitIdsForSettings(type.id)) {
      if (pinnedIdSet.has(id)) return true
    }
    return pinnedIdSet.has(type.id)
  }

  /** Template-backed non-core stay in catalog order; pin-only rows append last. */
  const includeNonCoreNatural = (type: FundingBenefitTypeItem) =>
    hasTemplateFor(type)

  const coreTypes = browsable
    .filter(isSettingsCoreProgram)
    .slice()
    .sort((a, b) => coreSortIndex(a) - coreSortIndex(b))

  const seenCoreLabels = new Set<string>()
  for (const type of coreTypes) {
    const label = displayFundingProgramLabel(type.program)
    if (seenCoreLabels.has(label.toLowerCase())) continue
    seenCoreLabels.add(label.toLowerCase())
    usedEligibleIds.add(type.id)
    const matched = automationByEligibleId.get(type.id)
    rows.push({
      key: matched ? automationKey(matched) : `core:${type.id}`,
      label,
      description: rowDescription(matched, type),
      eligibilityCriteria: rowEligibility(matched, type),
      automation: matched,
      eligible: type,
    })
  }

  for (const [eligibleId, program] of automationByEligibleId) {
    if (usedEligibleIds.has(eligibleId)) continue
    const type = browsable.find(t => t.id === eligibleId)
    if (!type || isSettingsCoreProgram(type)) continue
    if (!includeNonCoreNatural(type)) continue
    usedEligibleIds.add(eligibleId)
    rows.push({
      key: automationKey(program),
      label: displayFundingProgramLabel(type.program),
      description: rowDescription(program, type),
      eligibilityCriteria: rowEligibility(program, type),
      automation: program,
      eligible: type,
    })
  }

  for (const type of browsable) {
    if (usedEligibleIds.has(type.id)) continue
    if (isSettingsCoreProgram(type)) continue
    if (!includeNonCoreNatural(type)) continue
    usedEligibleIds.add(type.id)
    rows.push({
      key: `eligible:${type.id}`,
      label: displayFundingProgramLabel(type.program),
      description: rowDescription(undefined, type),
      eligibilityCriteria: rowEligibility(undefined, type),
      eligible: type,
    })
  }

  // Browse pins always append at the bottom, in the order they were added.
  for (const pinnedId of pinnedBenefitIds) {
    if (usedEligibleIds.has(pinnedId)) continue
    const type = browsable.find(t => {
      if (t.id === pinnedId) return true
      return relatedBenefitIdsForSettings(t.id).has(pinnedId)
    })
    if (!type || usedEligibleIds.has(type.id)) continue
    if (isSettingsCoreProgram(type)) continue
    if (!isPinned(type)) continue
    usedEligibleIds.add(type.id)
    const matched = automationByEligibleId.get(type.id)
    rows.push({
      key: matched ? automationKey(matched) : `eligible:${type.id}`,
      label: displayFundingProgramLabel(type.program),
      description: rowDescription(matched, type),
      eligibilityCriteria: rowEligibility(matched, type),
      automation: matched,
      eligible: type,
    })
  }

  return rows
}
